import { createEngine } from './createEngine';
import { ExecutionContext } from '../../../../services/backendServices/ViewModels';
import { DocumentApi } from '../../../../services/Document/Api';
import { RuntimeRule } from '../../CoreEngine/Utility/RuntimeRule';
import DropdownOptionsRepository from '../../../DocumentEditRules/PluginHost/services/dropdownOptionsRepository';
import { ContextInformationCollector } from './ContextInformationCollector';
import { getBuyerAndSupplierCompanyIdsByProfileType } from './getBuyerAndSupplierCompanyIdsByProfileType';
import { Touchpoint } from '../../ScriptingEngineEnvironment';
import { IWorkflowActivity, IDocumentUser } from '../../../DocumentEditRules/proservContractTypes';
import { createValidationFailureTypeDictionary, VFTDictionary } from './createValidationFailureTypeDictionary';
import { DocumentValidationConfiguration } from '../../../CJSRuleEngineV2ClientWrapper/ClientValidationController/DocumentValidationConfiguration';
import { ValidationFailureTypesApi } from '../../../../services/ValidationFailureTypes/Api';
import { BusinessDocType } from '../../../../types';
import { extractPONumbers, fetchPODocumentsForInvoice } from './extractPONumbers';
import { backendServices } from '../../../../services';

async function fetchDocument(documentId: number) {
    const documentApi = new DocumentApi();
    const { data: document } = await documentApi.getDocument(documentId);

    if (!document) {
        throw new Error('Could not fetch document');
    }

    return document;
}

async function fetchValidationFailureTypeDictionary(
    buyerCompanyId: number,
    businessDocType: BusinessDocType
): Promise<VFTDictionary> {
    const vftApi = new ValidationFailureTypesApi();

    const { data: validationFailureTypes } = await vftApi.getValidationFailureType({
        buyerCompanyId,
        $filter: `BusinessDocType eq ${businessDocType}`,
    });

    return createValidationFailureTypeDictionary(validationFailureTypes);
}

export async function executeValidationsForWorkflowEngine({ documentId }: { documentId: number }) {
    const document = await fetchDocument(documentId);

    // VendorClass currently only lives on the Invoice branch of BusinessDocFields.
    // For non-invoice doc types we run validations without a vendor class.
    const vendorClass =
        document.BusinessDocType === BusinessDocType.Invoice
            ? document.BusinessDocFields.Invoice.VendorClass
            : undefined;

    const { buyerCompanyId, supplierCompanyId } = getBuyerAndSupplierCompanyIdsByProfileType({
        senderProfileType: document.CommonFields.SenderProfileType,
        receiverProfileType: document.CommonFields.ReceiverProfileType,
        receiverCompanyId: document.CommonFields.ReceiverCompanyId,
        senderCompanyId: document.CommonFields.SenderCompanyId,
    });

    const contextInformationCollector = new ContextInformationCollector({
        type: 'client',
        event: {
            type: 'WorkflowLoadEvent',
        },
        previousExecution: null,
    });

    const vftDictionary = await fetchValidationFailureTypeDictionary(buyerCompanyId, document.BusinessDocType);

    const extractedPONumbers = extractPONumbers(document as unknown as backendServices.ViewModels.DocumentViewModel);

    const poDocuments = await fetchPODocumentsForInvoice(extractedPONumbers, buyerCompanyId);

    const cjsEngine = await createEngine({
        context: ExecutionContext.UserWorkflowOrWorkflowActivity,
        buyerCompanyId,
        senderCompanyId: supplierCompanyId, // ? is this property poorly named?
        vendorClass,
        inWorkflowEngine: true,
        createCachingService: Touchpoint.Caching.createFakeCachingService,
        workflowActivity: null,
        user: null,
        validationFailureTypeDictionary: vftDictionary,
    });

    const results = await cjsEngine.validateDocument({
        document,
        contextInformationCollector,
        poDocumentsForInvoice: poDocuments,
    });

    const { errors } = cjsEngine.transformToErrorAndWarningLists(results, document, poDocuments);

    return errors;
}

export async function executeDPSValidations(
    buyerCompanyId: number,
    senderCompanyId: number,
    documentId: number,
    dpsRuleName: string,
    runAfterDPSRule: boolean,
    vendorClass?: string,
    writeToLog?: (message: string) => void
) {
    const document = await fetchDocument(documentId);

    const contextInformationCollector = new ContextInformationCollector({ type: 'server' });

    const vftDictionary = await fetchValidationFailureTypeDictionary(buyerCompanyId, document.BusinessDocType);

    const extractedPONumbers = extractPONumbers(document as unknown as backendServices.ViewModels.DocumentViewModel);

    const poDocuments = await fetchPODocumentsForInvoice(extractedPONumbers, buyerCompanyId);

    const cjsEngine = await createEngine({
        context: ExecutionContext.DPSRule,
        buyerCompanyId,
        runAfterDPSRule,
        dpsRuleName,
        senderCompanyId,
        vendorClass,
        createCachingService: Touchpoint.Caching.createFakeCachingService,
        writeToLog,
        workflowActivity: null,
        user: null,
        validationFailureTypeDictionary: vftDictionary,
        businessDocType: document.BusinessDocType,
    });

    const results = document
        ? await cjsEngine.validateDocument({
              document,
              contextInformationCollector,
              poDocumentsForInvoice: poDocuments,
          })
        : [];

    const { errors } = cjsEngine.transformToErrorAndWarningLists(results, document, poDocuments);

    return errors;
}

interface ClientValidationData {
    context: ExecutionContext;
    buyerCompanyId: number;
    workflowId?: number;
    workflowActivityId?: number;
    senderCompanyId: number;
    vendorClass?: string;
    rulesFromClient: RuntimeRule[];
    dropdownRepository?: DropdownOptionsRepository;
    createCachingService: (scriptName: string) => Touchpoint.Caching.CachingService;
    childCompanyId?: number | null;
    workflowActivity: IWorkflowActivity | null;
    loggedInUser: IDocumentUser['userViewModel'] | null;
    validationFailureTypeDictionary: VFTDictionary;
    documentValidationConfiguration?: DocumentValidationConfiguration;
}

export async function createCJSEngineForUIValidation({
    context,
    buyerCompanyId,
    workflowId,
    workflowActivityId,
    senderCompanyId,
    vendorClass,
    rulesFromClient,
    dropdownRepository,
    createCachingService,
    childCompanyId,
    workflowActivity,
    loggedInUser,
    validationFailureTypeDictionary,
    documentValidationConfiguration,
}: ClientValidationData) {
    // for multi-company support
    const buyerCompanyIdToUse = childCompanyId ?? buyerCompanyId;
    const parentCompanyId = childCompanyId ? buyerCompanyId : undefined;

    const cjsEngine = await createEngine({
        context,
        buyerCompanyId: buyerCompanyIdToUse,
        workflowId,
        workflowActivityId,
        senderCompanyId,
        vendorClass,
        rulesFromClient,
        dropdownRepository,
        createCachingService,
        parentCompanyId,
        workflowActivity,
        user: loggedInUser,
        validationFailureTypeDictionary,
        documentValidationConfiguration,
    });

    return cjsEngine;
}
